/**
 * Product Dev — detail page (inline-edit refactor).
 *
 * No more global Edit mode. Each surface is directly editable in place:
 *   - Header (style name / style code / season): click-to-edit text fields
 *   - Status pill: dropdown of all 6 statuses
 *   - Stage strip: always clickable for admins
 *   - Details card: each row is click-to-edit with the appropriate input
 *     type (text / number / date / textarea / supplier picker)
 *   - Pricing & Costing cards: card-level pencil → all fields in the card
 *     enter edit mode together (multi-field transactions)
 *   - Variants: per-row inline editing + always-on "+ Colourway"
 *   - Attributes: per-kind inline editing
 *   - Samples & Files: already always-on
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { add, format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  Loader2,
  X,
  Plus,
  Trash2,
  Upload,
  Download,
  FileText,
  Archive,
  Image as ImageIcon,
  ChevronDown,
  Check,
  Circle,
  Copy,
  ShoppingCart,
  CalendarDays,
  AlertTriangle,
  Link2,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { BackToTopPill } from '@/components/BackToTopPill';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import {
  useProductDev,
  useProductDevVariants,
  useProductDevAttributes,
  useProductDevFiles,
  useProductDevMutations,
  useMentionableUsers,
} from '@/features/product-dev/hooks/useProductDev';
import {
  PRODUCT_DEV_STATUSES,
  PRODUCT_TYPES,
  STAGE_PILL_CLASS,
  STATUS_PILL_CLASS,
  DOC_TYPE_LABEL,
  NAMED_DOC_SLOTS,
  VARIANT_STATUS_ORDER,
  VARIANT_STATUS_PILL_CLASS,
  pipelineStagesForRecordType,
  type ProductDevPriority,
} from '@/features/product-dev/utils/status-style';
import { NON_DOCUMENT_DOC_TYPES } from '@/features/product-dev/utils/doc-types';
import {
  MAX_PRODUCT_DEV_UPLOAD_BYTES as MAX_UPLOAD_BYTES,
  formatProductDevUploadBytes as formatBytes,
  productDevFileToBase64 as fileToBase64,
  prepareProductDevUpload,
} from '@/features/product-dev/utils/file-upload';
import { PriorityFlag } from '@/features/product-dev/components/PriorityFlag';
import { pantoneToHex } from '@/features/product-dev/utils/pantone';
import { PantoneInput } from '@/features/product-dev/components/PantoneInput';
import { toRelativeDate, isOverdue } from '@/features/product-dev/utils/date-format';
import { CommentTimeline } from '@/features/comments/components/CommentTimeline';
import { StageProgressStrip } from '@/features/product-dev/components/StageProgressStrip';
import { CommercialsCard } from '@/features/product-dev/components/CommercialsCard';
import { BuyPlanCard } from '@/features/product-dev/components/BuyPlanCard';
import { SamplesCard } from '@/features/product-dev/components/SamplesCard';
import { InlineEdit, InlineSelect } from '@/features/product-dev/components/InlineEdit';
import { InlineProductSearch } from '@/features/product-dev/components/InlineProductSearch';
import { TrimsCard } from '@/features/product-dev/components/TrimsCard';
import { RelatedRecordsCard } from '@/features/product-dev/components/RelatedRecordsCard';
import { AddToPurchaseOrderDialog } from '@/features/product-dev/components/AddToPurchaseOrderDialog';
import { CardRowsSkeleton } from '@/features/product-dev/components/skeletons';
import { PageLoader } from '@/components/loading/PageLoader';

const ATTRIBUTE_KINDS = ['dye_method', 'wash', 'embellishment', 'trim_type'] as const;
type AttributeKind = (typeof ATTRIBUTE_KINDS)[number];

// trim_type data + type stay intact; it's just hidden from the Attributes card
// display (trims are now their own card). Mirrors the DISPLAY_DOC_SLOTS filter.
const DISPLAY_ATTRIBUTE_KINDS: readonly AttributeKind[] = ATTRIBUTE_KINDS.filter(
  (k) => k !== 'trim_type',
);

const DOC_TYPE_OPTIONS = Object.keys(DOC_TYPE_LABEL);

// Sample photos live with the Samples card — filter them out of the Files card
// display (do NOT touch NAMED_DOC_SLOTS itself or the hero upload path).
const DISPLAY_DOC_SLOTS: readonly string[] = NAMED_DOC_SLOTS.filter(
  (s) => s !== 'sample_photo',
);

// ----------------------------------------------------------------------------
// VariantStatusPill — per-colourway approval, click-cycles through the order.
// Persists through the page's setVariants (same clobber-safe whole-array path).
// ----------------------------------------------------------------------------

function VariantStatusPill({
  status,
  canEdit,
  onCycle,
}: {
  status: string;
  canEdit: boolean;
  onCycle: (next: string) => void;
}) {
  const { t } = useTranslation('productDev');
  const cls = VARIANT_STATUS_PILL_CLASS[status] ?? 'bg-muted text-muted-foreground';
  const label = t(`variantStatus.${status}`);
  if (!canEdit) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
          cls,
        )}
      >
        {label}
      </span>
    );
  }
  const idx = VARIANT_STATUS_ORDER.indexOf(status);
  const next = VARIANT_STATUS_ORDER[(idx + 1) % VARIANT_STATUS_ORDER.length]!;
  return (
    <button
      type="button"
      onClick={() => onCycle(next)}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium cursor-pointer transition-opacity hover:opacity-80',
        cls,
      )}
      aria-label={t('detail.aria.cycleVariantStatus', { status: label })}
      title={t('detail.variants.statusCycleHint')}
    >
      {label}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Size ladder + client-side expansion. Mirrors the backend setVariants
// self-heal EXACTLY (canonical ladder XS..5XL; XXL→2XL, XXXL→3XL aliases;
// non-ladder tokens like '2T' or '32' pass through untouched). The client
// expansion just makes the tokenised UX feel immediate — the server re-heals
// on persist, so the two can never disagree.
// ----------------------------------------------------------------------------

const SIZE_LADDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] as const;
const SIZE_ALIASES: Record<string, string> = {
  XXL: '2XL',
  XXXL: '3XL',
  XXXXL: '4XL',
  XXXXXL: '5XL',
};

/** Resolve a single raw token to its canonical ladder value, or pass it
 *  through verbatim when it isn't a ladder size. */
const canonicalSize = (raw: string): string => {
  const up = raw.trim().toUpperCase();
  const aliased = SIZE_ALIASES[up] ?? up;
  return (SIZE_LADDER as readonly string[]).includes(aliased) ? aliased : raw.trim();
};

/** Expand a "A-B" range into the inclusive ladder slice, or null when either
 *  endpoint isn't a ladder size (so "2-XL" or odd hyphenated tokens fall back
 *  to passthrough handling). */
const expandRange = (token: string): string[] | null => {
  const parts = token.split('-');
  if (parts.length !== 2) return null;
  const a = canonicalSize(parts[0]!);
  const b = canonicalSize(parts[1]!);
  const ia = (SIZE_LADDER as readonly string[]).indexOf(a);
  const ib = (SIZE_LADDER as readonly string[]).indexOf(b);
  if (ia === -1 || ib === -1) return null;
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia];
  return SIZE_LADDER.slice(lo, hi + 1);
};

/** Expand a free-text sizes string (comma- and/or space-separated, with range
 *  shorthand) into a de-duplicated, order-preserving list of sizes. */
const expandSizeInput = (raw: string): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)) {
    const pieces = expandRange(token) ?? [canonicalSize(token)];
    for (const p of pieces) {
      if (!seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
  }
  return out;
};

/** Flip one ladder size in a free-text sizes draft, preserving custom tokens
 *  (rebuilds from the expanded set so ranges/aliases stay resolved). */
const toggleSizeToken = (draft: string, size: string): string => {
  const current = expandSizeInput(draft);
  const next = current.includes(size)
    ? current.filter((s) => s !== size)
    : [...current, size];
  return next.join(' ');
};

/** Every colourway starts on the standard retail run (owner request) — one
 *  chip-tap removes an edge size, instead of typing the whole ladder in. */
const DEFAULT_NEW_COLOURWAY_SIZES = 'XS S M L XL 2XL 3XL';

/**
 * Single convert→then-caller-size-checks seam for every upload chokepoint on
 * this page. Converts iPhone HEIC/HEIF to JPEG (lazy-loaded decoder) BEFORE the
 * caller's size validation runs (conversion can inflate the byte size). On a
 * conversion failure it returns the ORIGINAL file (non-lossy) and toasts a
 * gentle "won't preview" warning; the caller still uploads it.
 */
async function prepareUpload(file: File, t: TFunction): Promise<File> {
  const { file: prepared, conversionFailed } = await prepareProductDevUpload(file);
  if (conversionFailed) {
    toast({
      title: t('detail.toast.heicFailed.title'),
      description: t('detail.toast.heicFailed.desc'),
    });
  }
  return prepared;
}

/** Uppercase file extension (max 4 chars) for the Files-card fallback tile. */
const fileExtLabel = (name: string): string => {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return (m?.[1] ?? 'FILE').toUpperCase().slice(0, 4);
};

const initials = (name: string | null, fallback = '?') => {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter((p) => p);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts.at(-1)?.[0] ?? '')).toUpperCase();
};

type UpdatePayload = Parameters<
  ReturnType<typeof useProductDevMutations>['update']['mutateAsync']
>[0]['updates'];

export default function ProductDevDetail() {
  const { t } = useTranslation('productDev');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const productDevQuery = useProductDev(id);
  const variantsQuery = useProductDevVariants(id);
  const attributesQuery = useProductDevAttributes(id);
  const filesQuery = useProductDevFiles(id);
  const { suppliers } = useSuppliers();
  const {
    update,
    archive,
    setVariantsSerialized,
    setVariantsQueueDepth,
    getCachedVariants,
    optimisticVariantStatus,
    setAttributes,
    uploadFile,
    deleteFile,
    uploadVariantPhoto,
    duplicate,
    addToPurchaseOrder,
  } = useProductDevMutations(id);
  const [addToPoOpen, setAddToPoOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Pending record-type conversion (task → change / new product). Null closed.
  const [convertTo, setConvertTo] = useState<'product_change' | 'new_product' | null>(
    null,
  );
  const { organization } = useOrganization();
  const utils = trpc.useUtils();
  // Serializes field-update writes in click order (see updateField below).
  // Declared here, above the loading/error early returns (rules-of-hooks).
  const updateQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Picker data sources (cheap, infrequent fetches).
  const posQuery = trpc.purchaseOrders.getOrders.useQuery(
    {},
    { staleTime: 5 * 60_000, refetchOnWindowFocus: false },
  );
  const shipmentsQuery = trpc.shipments.listShipments.useQuery(
    { limit: 200 },
    { staleTime: 5 * 60_000, refetchOnWindowFocus: false },
  );
  const mentionableUsersQuery = useMentionableUsers('product_dev');

  // Buy-plan grand total (sum of every colour × size cell) — fed to the
  // Commercials card's projected-buy-cost line. Computed from the server
  // variants query so it stays a single source of truth with the BuyPlan grid.
  const buyPlanGrandTotal = useMemo(() => {
    let total = 0;
    for (const v of variantsQuery.data ?? []) {
      for (const s of v.sizes) total += v.size_quantities?.[s] ?? 0;
    }
    return total;
  }, [variantsQuery.data]);

  if (productDevQuery.isLoading) {
    return <PageLoader pool="productDev" />;
  }

  if (productDevQuery.error || !productDevQuery.data) {
    return (
      <div className="w-full py-12 px-4 sm:px-6 lg:px-8">
        <p className="text-sm text-destructive">
          {productDevQuery.error?.message ?? t('detail.error.notFound')}
        </p>
        <Button variant="ghost" onClick={() => navigate('/product-dev')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('detail.action.backToList')}
        </Button>
      </div>
    );
  }

  const product = productDevQuery.data;
  const canEdit = isAdmin();
  // Tasks ("Fix collar on all cuts") have no product spec — hide the
  // spec-only surfaces (product type, fabric/gsm/construction, pricing,
  // costing, samples, variants, attributes) and run the task stage chain.
  const isTask = product.record_type === 'task';

  // Field updates are SERIALIZED in click order (review finding): with the
  // isPending gates removed for instant paint, two rapid re-picks of the SAME
  // field would otherwise race as concurrent POSTs and commit in arbitrary
  // order — a superseded value could win silently. The optimistic paint is
  // component-local so it stays instant; only the network writes queue. Each
  // link swallows its own rejection AFTER rethrowing to the caller (which
  // handles the toast/revert), so the chain can never wedge.
  const updateField = (updates: UpdatePayload) => {
    const run = updateQueueRef.current.then(() =>
      update.mutateAsync({ productDevId: product.id, updates }).then(() => undefined),
    );
    updateQueueRef.current = run.catch(() => undefined);
    return run;
  };

  // Trim image upload seam — reuses the page's single upload chokepoint
  // (`uploadFile` + the base64 / size-validation helpers) so TrimsCard doesn't
  // duplicate them. Uploads as doc_type 'trim_image'; resolves the new file id
  // for the card to write into the trim's `image_file_id` via setTrims.
  const handleTrimImageUpload = async (raw: File): Promise<string | null> => {
    const file = await prepareUpload(raw, t);
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: t('detail.toast.fileTooLarge.title', { size: formatBytes(file.size) }),
        description: t('detail.toast.fileTooLarge.desc', { max: formatBytes(MAX_UPLOAD_BYTES) }),
        variant: 'destructive',
      });
      return null;
    }
    try {
      const { body, type } = await fileToBase64(file);
      const created = await uploadFile.mutateAsync({
        productDevId: product.id,
        docType: 'trim_image',
        fileName: file.name,
        contentType: type,
        body,
      });
      return created.id;
    } catch (err) {
      toast({
        title: t('detail.toast.photoUploadFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
      return null;
    }
  };

  const runArchive = async () => {
    setArchiveOpen(false);
    try {
      await archive.mutateAsync({ productDevId: product.id });
      navigate('/product-dev');
    } catch (err) {
      toast({
        title: t('detail.toast.archiveFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  // Convert a task into a garment record (product_change / new_product). The
  // backend normalizes stage (resets to Brief) + product_type; the page then
  // re-renders with the spec-only surfaces unlocked.
  const runConvert = async () => {
    const next = convertTo;
    setConvertTo(null);
    if (!next) return;
    try {
      await update.mutateAsync({
        productDevId: product.id,
        // `record_type` is being added to the update mutation input by a
        // parallel backend change; cast until the shared type lands. The key
        // is still sent on the wire.
        updates: { record_type: next } as unknown as UpdatePayload,
      });
    } catch (err) {
      toast({
        title: t('detail.toast.convertFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handleDuplicate = async () => {
    try {
      const created = await duplicate.mutateAsync({
        productDevId: product.id,
      });
      navigate(`/product-dev/${created.id}`);
      toast({
        title: t('detail.toast.duplicated.title'),
        description: t('detail.toast.duplicated.desc', { name: created.style_name }),
      });
    } catch (err) {
      toast({
        title: t('detail.toast.duplicateFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (next: string) => {
    try {
      await update.mutateAsync({
        productDevId: product.id,
        updates: { status: next },
      });
    } catch (err) {
      toast({
        title: t('detail.toast.statusFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handleStageChange = async (next: string) => {
    try {
      await update.mutateAsync({
        productDevId: product.id,
        updates: { stage: next },
      });
    } catch (err) {
      toast({
        title: t('detail.toast.stageFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6 animate-enter">
      {/* Back */}
      <Button variant="ghost" asChild className="-ml-2">
        <Link to="/product-dev">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('detail.action.back')}
        </Link>
      </Button>

      {/* Header block */}
      <div className="flex items-start gap-5 flex-wrap">
        {/* Hero thumbnail — click-to-upload. The file uploads as a
            sample_photo doc AND is pinned as hero_file_id, so a later
            colourway photo can never displace the operator's chosen style
            photo (meeting feedback 2026-07-07: "adding a colour overtakes
            the style thumbnail" — sample_photo alone ranks BELOW variant
            photos in the list-thumbnail fallback chain). */}
        <HeroThumbnail
          productDevId={product.id}
          heroUrl={product.hero_image_url}
          altText={product.style_name}
          canEdit={canEdit}
          uploadFile={uploadFile}
          onSetHero={(fileId) => updateField({ hero_file_id: fileId })}
        />

        {/* Identity */}
        <div className="flex-1 min-w-0">
          {/* SKU / legacy style-code identifier (mono pill). The buyer-facing
              language follows the warehouse convention ("SKU"), while the
              existing `style_code` column remains the style-level identifier;
              real inventory `sku_id` linkage still comes from the graduated
              Shopify product's exact colour + size match during PO handoff.
              Root cause of the old truncation: a fixed
              cap (`max-w-[280px]`) plus InlineEdit's `w-full` button + inner
              `truncate` span meant the fit-content wrapper never got room, so
              even the short placeholder clipped. Fix: a comfortable min-width
              floor, so short codes and the placeholder read cleanly, with
              `w-fit` + `max-w-full` so a
              long code like "HW-HOODIE-OS-2026-V2" grows to show in full without
              editing, and only extreme overflow past the column truncates. The
              edit input inherits the same floor, so it's comfortable too. */}
          <div className="mb-1.5 w-fit min-w-[15rem] max-w-full">
            <InlineEdit
              value={product.style_code}
              onSave={(v) => updateField({ style_code: v })}
              placeholder={t('detail.placeholder.styleCode')}
              readOnly={!canEdit}
              displayClassName="font-mono text-[11px] text-muted-foreground tracking-wider uppercase"
              display={(v) => (
                <span className="bg-muted px-2 py-0.5 rounded-md">{v}</span>
              )}
              inputClassName="font-mono text-[11px] uppercase tracking-wider"
              maxLength={60}
            />
          </div>

          {/* Style name (H1) */}
          <h1 className="text-2xl font-semibold leading-tight">
            <InlineEdit
              value={product.style_name}
              onSave={(v) => updateField({ style_name: v ?? product.style_name })}
              placeholder={t('detail.placeholder.styleName')}
              readOnly={!canEdit}
              displayClassName="text-2xl font-semibold leading-tight"
              inputClassName="text-2xl font-semibold h-10"
            />
          </h1>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Season chip */}
            <div className="inline-block min-w-[80px]">
              <InlineEdit
                value={product.season}
                onSave={(v) => updateField({ season: v })}
                placeholder={t('detail.placeholder.season')}
                readOnly={!canEdit}
                displayClassName="text-[11px] font-semibold uppercase tracking-wide"
                display={(v) => (
                  <span className="bg-threadheads-purple/10 text-threadheads-purple px-2 py-0.5 rounded-md">
                    {v}
                  </span>
                )}
                inputClassName="text-[11px] uppercase tracking-wide h-7"
                maxLength={40}
              />
            </div>

            {/* Stage pill */}
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                STAGE_PILL_CLASS[product.stage] ?? 'bg-muted text-muted-foreground',
              )}
            >
              {t(`stage.${product.stage}`)}
            </span>

            {/* Status pill — dropdown for admins */}
            {canEdit ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer',
                      STATUS_PILL_CLASS[product.status] ?? 'bg-muted text-muted-foreground',
                    )}
                    aria-label={t('detail.aria.changeStatus')}
                  >
                    {t(`status.${product.status}`)}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {PRODUCT_DEV_STATUSES.map((s) => (
                    <DropdownMenuItem
                      key={s}
                      onClick={() => void handleStatusChange(s)}
                      disabled={s === product.status}
                      className="flex items-center justify-between gap-2 cursor-pointer"
                    >
                      <span>{t(`status.${s}`)}</span>
                      {s === product.status && <Check className="h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                  STATUS_PILL_CLASS[product.status] ?? 'bg-muted text-muted-foreground',
                )}
              >
                {t(`status.${product.status}`)}
              </span>
            )}

            {/* Triage flag — one-click level menu; tooltip names the level.
                Returns the save promise so a failed save reverts the paint. */}
            <PriorityFlag
              priority={product.priority}
              canEdit={canEdit}
              onCycle={(next: ProductDevPriority | null) =>
                updateField({ priority: next }).catch((err) => {
                  toast({
                    title: t('priority.saveFailed'),
                    description:
                      err instanceof Error ? err.message : t('detail.toast.tryAgain'),
                    variant: 'destructive',
                  });
                  throw err; // PriorityFlag reverts its optimistic paint
                })
              }
            />

            {/* Product type — tasks have none, show the record type instead.
                Admins get a "Convert to change" affordance so a task can
                graduate into a spec-bearing garment record. */}
            {isTask ? (
              canEdit ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                      aria-label={t('detail.aria.convertRecordType')}
                    >
                      · {t(`recordType.${product.record_type}`)}
                      <ChevronDown className="h-3 w-3 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuItem onClick={() => setConvertTo('product_change')}>
                      {t('detail.convert.toChange')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setConvertTo('new_product')}>
                      {t('detail.convert.toNewProduct')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <span className="text-xs text-muted-foreground">
                  · {t(`recordType.${product.record_type}`)}
                </span>
              )
            ) : canEdit ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    aria-label={t('detail.aria.changeProductType')}
                  >
                    · {product.product_type ? t(`productType.${product.product_type}`) : '-'}
                    {/* Always show the record type — "New product" was hidden
                        as the implied default, which read as missing data
                        (owner feedback 2026-07-07). */}
                    {` · ${t(`recordType.${product.record_type}`)}`}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {PRODUCT_TYPES.map((pt) => (
                    <DropdownMenuItem
                      key={pt}
                      onClick={() => void updateField({ product_type: pt })}
                      disabled={product.product_type === pt}
                    >
                      {t(`productType.${pt}`)}
                    </DropdownMenuItem>
                  ))}
                  {/* Garment ↔ garment record-type switch: no stage/spec side
                      effects server-side, so no confirm needed — a record can
                      move between "new product" and "change" freely instead of
                      being recreated (owner ask). Task is a one-way door and
                      never offered here. */}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t('detail.convert.recordTypeLabel')}
                  </DropdownMenuLabel>
                  {(['new_product', 'product_change'] as const).map((rt) => (
                    <DropdownMenuItem
                      key={rt}
                      onClick={() =>
                        void updateField({ record_type: rt } as unknown as UpdatePayload).catch(
                          (err) => {
                            toast({
                              title: t('detail.toast.convertFailed.title'),
                              description:
                                err instanceof Error ? err.message : t('detail.toast.tryAgain'),
                              variant: 'destructive',
                            });
                          },
                        )
                      }
                      disabled={product.record_type === rt}
                    >
                      {t(`recordType.${rt}`)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="text-xs text-muted-foreground">
                · {product.product_type ? t(`productType.${product.product_type}`) : '-'}
                {` · ${t(`recordType.${product.record_type}`)}`}
              </span>
            )}
          </div>

          {product.assigned_to_name && (
            <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
              <span className="inline-flex h-6 w-6 rounded-full overflow-hidden bg-threadheads-purple/10 text-threadheads-purple text-[10px] font-semibold items-center justify-center shrink-0">
                {product.assigned_to_avatar_url ? (
                  <img
                    src={product.assigned_to_avatar_url}
                    alt={product.assigned_to_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials(product.assigned_to_name)
                )}
              </span>
              <span className="text-foreground">{product.assigned_to_name}</span>
            </div>
          )}
        </div>

        {/* Action cluster — Add to PO + Duplicate + Archive. Everything else is inline. */}
        {canEdit && (
          <div className="flex items-center gap-2 ml-auto">
            {!isTask && (
              <Button
                variant="outline"
                onClick={() => setAddToPoOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" /> {t('detail.action.addToPurchaseOrder')}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => void handleDuplicate()}
              disabled={duplicate.isPending}
            >
              {duplicate.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}{' '}
              {t('detail.action.duplicate')}
            </Button>
            <Button variant="ghost" onClick={() => setArchiveOpen(true)}>
              <Archive className="h-4 w-4 mr-2" /> {t('detail.action.archive')}
            </Button>
          </div>
        )}
      </div>

      {/* Stage progress strip — always clickable for admins. The chain is
          record-type-aware: garment pipeline vs open → in_progress → done. */}
      <StageProgressStrip
        currentStage={product.stage}
        canEdit={canEdit}
        stages={pipelineStagesForRecordType(product.record_type)}
        onChange={(next) => void handleStageChange(next)}
      />

      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-6">
          <DeadlineCard product={product} canEdit={canEdit} onUpdate={updateField} />

          <DetailsCard
            product={product}
            isTask={isTask}
            canEdit={canEdit}
            brands={
              // `brands` is being added to org settings by a parallel backend
              // change; read defensively until the shared type lands.
              (organization?.settings as { brands?: string[] } | null | undefined)
                ?.brands ?? []
            }
            suppliers={suppliers.map((s) => ({ id: s.id, title: s.title }))}
            purchaseOrders={(posQuery.data?.data ?? []).map((po) => ({
              id: po.id,
              label: po.po_number ?? po.title ?? po.id.slice(0, 8),
            }))}
            shipments={(shipmentsQuery.data?.items ?? []).map((s) => ({
              id: s.id,
              label: s.shipment_number,
            }))}
            members={(mentionableUsersQuery.data ?? []).map((u) => ({
              id: u.id,
              label: u.display_name,
            }))}
            onUpdate={updateField}
          />

          {/* Samples sit above Commercials (owner request): sampling drives the
              commercial conversation, so it reads first. */}
          {!isTask && <SamplesCard productDevId={product.id} canEdit={canEdit} />}

          {!isTask && (
            <CommercialsCard
              product={product}
              onSave={updateField}
              canEdit={canEdit}
              buyPlanGrandTotal={buyPlanGrandTotal}
            />
          )}

          {/* Buy plan sits directly under Commercials — the quantities feed the
              projected buy cost the Commercials card shows. */}
          {!isTask && (
            <BuyPlanCard
              productDevId={product.id}
              product={product}
              canEdit={canEdit}
              variantsQuery={variantsQuery}
              setVariantsSerialized={setVariantsSerialized}
              setVariantsQueueDepth={setVariantsQueueDepth}
              getCachedVariants={getCachedVariants}
            />
          )}
        </div>

        {/* Right col */}
        <div className="space-y-6">
          {!isTask && (
            <VariantsCard
              productDevId={product.id}
              canEdit={canEdit}
              variantsQuery={variantsQuery}
              setVariantsSerialized={setVariantsSerialized}
              setVariantsQueueDepth={setVariantsQueueDepth}
              getCachedVariants={getCachedVariants}
              optimisticVariantStatus={optimisticVariantStatus}
              uploadVariantPhoto={uploadVariantPhoto}
            />
          )}
          {/* Related records sit directly under Variants (owner request) —
              they're navigational context, not tail-end metadata. */}
          {!isTask && (
            <RelatedRecordsCard productDevId={product.id} canEdit={canEdit} />
          )}
          {!isTask && (
            <AttributesCard
              productDevId={product.id}
              canEdit={canEdit}
              attributesQuery={attributesQuery}
              setAttributes={setAttributes}
            />
          )}
          {!isTask && (
            <TrimsCard
              productDevId={product.id}
              canEdit={canEdit}
              onUploadImage={handleTrimImageUpload}
            />
          )}
          <FilesCard
            productDevId={product.id}
            canEdit={canEdit}
            filesQuery={filesQuery}
            uploadFile={uploadFile}
            deleteFile={deleteFile}
            utils={utils}
          />
        </div>
      </div>

      {/* Activity timeline — full-width at the very bottom of the page (owner
          request), below both columns. */}
      <CommentTimeline entityType="product_dev" entityId={product.id} />

      {!isTask && (
        <AddToPurchaseOrderDialog
          open={addToPoOpen}
          onOpenChange={setAddToPoOpen}
          product={{
            id: product.id,
            stage: product.stage,
            status: product.status,
            price_agreed_by_supplier: product.price_agreed_by_supplier,
            price_agreed_by_us: product.price_agreed_by_us,
            supplier_id: product.supplier_id,
            supplier_title: product.supplier_title,
            agreed_price: product.agreed_price,
            agreed_currency: product.agreed_currency,
            moq: product.moq,
            proposed_quantity: product.proposed_quantity,
          }}
          addToPurchaseOrder={addToPurchaseOrder}
          update={update}
        />
      )}

      {/* Archive confirmation */}
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.confirm.archive.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.confirm.archive.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('detail.confirm.archive.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void runArchive()}
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" /> {t('detail.confirm.archive.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task → change conversion confirmation */}
      <AlertDialog open={convertTo !== null} onOpenChange={(o) => !o && setConvertTo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {convertTo === 'new_product'
                ? t('detail.convert.confirm.newProductTitle')
                : t('detail.convert.confirm.changeTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.convert.confirm.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('detail.convert.confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={() => void runConvert()}
            >
              {t('detail.convert.confirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BackToTopPill />
    </div>
  );
}

// ----------------------------------------------------------------------------
// ChipLink — a linked entity (PO / shipment) rendered as a real chip, not
// bare underlined text. Stops row-click propagation so the link navigates
// cleanly out of the inline-edit affordance.
// ----------------------------------------------------------------------------

function ChipLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium text-foreground transition-colors hover:border-threadheads-purple/40 hover:text-threadheads-purple"
    >
      <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{children}</span>
    </Link>
  );
}

// ----------------------------------------------------------------------------
// DeadlineCard — the expected-completion widget promoted out of the Details
// list. Reads as "the deadline": large date, overdue state surfaced inline
// (rose when past due unless the status is terminal/parked), a proper shadcn
// Calendar + Popover picker, and quick-set affordances.
// ----------------------------------------------------------------------------

function DeadlineCard({
  product,
  canEdit,
  onUpdate,
}: {
  product: NonNullable<ReturnType<typeof useProductDev>['data']>;
  canEdit: boolean;
  onUpdate: (updates: UpdatePayload) => Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const iso = product.expected_completion_date;
  const selected = iso ? parseISO(iso) : undefined;
  const rel = iso ? toRelativeDate(iso, new Date()) : null;
  const overdue = isOverdue(iso, product.status);

  const commit = (date: Date | null) => {
    // Optimistic: the cache patch paints the new date immediately, so close
    // the popover now and let the save settle in the background — a failure
    // rolls the cache back and toasts (mirrors InlineEdit's commit).
    setOpen(false);
    setSaving(true);
    onUpdate({
      expected_completion_date: date ? format(date, 'yyyy-MM-dd') : null,
    })
      .catch((err) => {
        toast({
          title: t('deadline.saveFailed'),
          description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
          variant: 'destructive',
        });
      })
      .finally(() => setSaving(false));
  };

  const QUICK_SETS: Array<{ key: string; duration: Parameters<typeof add>[1] }> = [
    { key: 'deadline.quickWeek', duration: { weeks: 1 } },
    { key: 'deadline.quickTwoWeeks', duration: { weeks: 2 } },
    { key: 'deadline.quickMonth', duration: { months: 1 } },
  ];

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center gap-3.5">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
              overdue
                ? 'bg-rose-100 text-rose-700'
                : 'bg-threadheads-purple/10 text-threadheads-purple',
            )}
          >
            <CalendarDays className="h-5 w-5" aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t('deadline.title')}
            </div>
            {iso && selected ? (
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className={cn(
                    'text-lg font-semibold tabular-nums',
                    overdue && 'text-rose-700',
                  )}
                >
                  {format(selected, 'd MMM yyyy')}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-xs',
                    overdue ? 'font-medium text-rose-700' : 'text-muted-foreground',
                  )}
                  title={rel?.absolute}
                >
                  {overdue && <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                  {overdue ? t('deadline.overdue') : t('deadline.due', { when: rel?.short })}
                </span>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t('deadline.none')}</div>
            )}
          </div>

          {canEdit && (
            <Popover open={open} onOpenChange={(o) => !saving && setOpen(o)}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0" disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <>
                      <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
                      {iso ? t('deadline.change') : t('deadline.set')}
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex flex-wrap gap-1.5 border-b p-2">
                  {QUICK_SETS.map((q) => (
                    <Button
                      key={q.key}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => void commit(add(new Date(), q.duration))}
                    >
                      {t(q.key)}
                    </Button>
                  ))}
                </div>
                <Calendar
                  mode="single"
                  selected={selected}
                  onSelect={(date) => date && void commit(date)}
                  initialFocus
                  className="pointer-events-auto"
                />
                {iso && (
                  <div className="border-t p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-full px-2 text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => void commit(null)}
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" /> {t('deadline.clear')}
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Details — every row is click-to-edit
// ----------------------------------------------------------------------------

function DetailsCard({
  product,
  isTask,
  canEdit,
  brands,
  suppliers,
  purchaseOrders,
  shipments,
  members,
  onUpdate,
}: {
  product: NonNullable<ReturnType<typeof useProductDev>['data']>;
  /** Tasks hide the product-spec-only rows (fabric / gsm / construction). */
  isTask: boolean;
  canEdit: boolean;
  /** Org-configured brand list for the Brand dropdown (may be empty). */
  brands: string[];
  suppliers: Array<{ id: string; title: string }>;
  purchaseOrders: Array<{ id: string; label: string }>;
  shipments: Array<{ id: string; label: string }>;
  members: Array<{ id: string; label: string }>;
  onUpdate: (updates: UpdatePayload) => Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-2 items-center border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="text-sm min-w-0">{children}</div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('detail.section.details')}</CardTitle>
      </CardHeader>
      <CardContent>
        {!isTask && (
          <>
            <Row label={t('detail.field.brand')}>
              <BrandField
                value={product.brand}
                brands={brands}
                canEdit={canEdit}
                onSave={(v) => onUpdate({ brand: v })}
              />
            </Row>
            <Row label={t('detail.field.capsule')}>
              <InlineEdit
                value={product.capsule}
                onSave={(v) => onUpdate({ capsule: v })}
                placeholder={t('detail.placeholder.addCapsule')}
                readOnly={!canEdit}
                maxLength={120}
              />
            </Row>
          </>
        )}
        <Row label={t('detail.field.supplier')}>
          <InlineSelect
            value={product.supplier_id}
            onSave={(v) => onUpdate({ supplier_id: v })}
            options={suppliers.map((s) => ({ value: s.id, label: s.title }))}
            placeholder={t('detail.placeholder.addSupplier')}
            readOnly={!canEdit}
            display={(_v, label) => label ?? '-'}
          />
        </Row>
        <Row label={t('detail.field.assignedTo')}>
          <InlineSelect
            value={product.assigned_to}
            onSave={(v) => onUpdate({ assigned_to: v })}
            options={members.map((m) => ({ value: m.id, label: m.label }))}
            placeholder={t('detail.placeholder.assignSomeone')}
            readOnly={!canEdit}
            display={(_v, label) => label ?? product.assigned_to_name ?? '-'}
          />
        </Row>
        <Row label={t('detail.field.linkedPo')}>
          <InlineSelect
            value={product.linked_purchase_order_id}
            onSave={(v) => onUpdate({ linked_purchase_order_id: v })}
            options={purchaseOrders.map((p) => ({ value: p.id, label: p.label }))}
            placeholder={t('detail.placeholder.linkPo')}
            readOnly={!canEdit}
            display={(v, label) =>
              v ? (
                <ChipLink to={`/orders/${v}`}>
                  {label ?? t('detail.action.viewOrder')}
                </ChipLink>
              ) : (
                <span className="text-muted-foreground">-</span>
              )
            }
          />
        </Row>
        <Row label={t('detail.field.linkedShipment')}>
          <InlineSelect
            value={product.linked_shipment_id}
            onSave={(v) => onUpdate({ linked_shipment_id: v })}
            options={shipments.map((s) => ({ value: s.id, label: s.label }))}
            placeholder={t('detail.placeholder.linkShipment')}
            readOnly={!canEdit}
            display={(v, label) =>
              v ? (
                <ChipLink to={`/shipments/${v}`}>
                  {label ?? product.linked_shipment_reference ?? t('detail.action.viewShipment')}
                </ChipLink>
              ) : (
                <span className="text-muted-foreground">-</span>
              )
            }
          />
        </Row>
        {!isTask && (
          <>
            <Row label={t('detail.field.shopifyProduct')}>
              <InlineProductSearch
                value={product.graduated_product_id}
                valueTitle={product.graduated_product_title}
                onSave={(v) => onUpdate({ graduated_product_id: v })}
                readOnly={!canEdit}
              />
            </Row>
            <Row label={t('detail.field.fabricComposition')}>
              <InlineEdit
                value={product.fabric_composition}
                onSave={(v) => onUpdate({ fabric_composition: v })}
                placeholder={t('detail.placeholder.fabricComposition')}
                readOnly={!canEdit}
              />
            </Row>
            <Row label={t('detail.field.weight')}>
              <InlineEdit
                type="number"
                value={product.fabric_weight_gsm == null ? null : String(product.fabric_weight_gsm)}
                onSave={(v) =>
                  onUpdate({
                    fabric_weight_gsm: v == null ? null : Number.parseInt(v, 10),
                  })
                }
                placeholder={t('detail.placeholder.addGsm')}
                readOnly={!canEdit}
                min={0}
                step="1"
                display={(v) =>
                  v ? t('detail.unit.gsm', { value: v }) : <span className="text-muted-foreground">-</span>
                }
              />
            </Row>
            <Row label={t('detail.field.construction')}>
              <InlineEdit
                value={product.construction}
                onSave={(v) => onUpdate({ construction: v })}
                placeholder={t('detail.placeholder.construction')}
                readOnly={!canEdit}
              />
            </Row>
          </>
        )}
        <Row label={t('detail.field.notes')}>
          <InlineEdit
            type="textarea"
            value={product.construction_notes}
            onSave={(v) => onUpdate({ construction_notes: v })}
            placeholder={t('detail.placeholder.addNotes')}
            readOnly={!canEdit}
            rows={3}
          />
        </Row>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// BrandField — brand row: an InlineSelect fed by the org's configured brands
// (Settings → Store → Brands) plus the record's current value (so a legacy
// brand not in the org list still displays + stays selectable), with a clear /
// None option. Free-text custom entry was retired — brands are now curated in
// Settings, so when the org has none configured we point the user there instead
// of an ad-hoc input. Mirrors the sibling InlineSelect fields (Supplier etc.).
// ----------------------------------------------------------------------------

function BrandField({
  value,
  brands,
  canEdit,
  onSave,
}: {
  value: string | null;
  brands: string[];
  canEdit: boolean;
  onSave: (next: string | null) => Promise<void> | void;
}) {
  const { t } = useTranslation('productDev');

  // Union of org brands + the current value (so a legacy brand not in the org
  // list still appears + stays selectable), order-preserving + de-duplicated.
  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ value: string; label: string }> = [];
    for (const b of [...brands, ...(value ? [value] : [])]) {
      const v = b.trim();
      if (v && !seen.has(v)) {
        seen.add(v);
        out.push({ value: v, label: v });
      }
    }
    return out;
  }, [brands, value]);

  // No brands configured and nothing set yet: guide the user to Settings rather
  // than a free-text input (custom entry retired).
  if (options.length === 0) {
    if (!canEdit) return <span className="text-muted-foreground">-</span>;
    return (
      <Link
        to="/settings?tab=organization"
        className="inline-flex items-center text-sm italic text-muted-foreground rounded px-1.5 py-1 -mx-1.5 transition-colors hover:text-threadheads-purple"
      >
        {t('detail.brand.noBrandsHint')}
      </Link>
    );
  }

  return (
    <InlineSelect
      value={value}
      onSave={onSave}
      options={options}
      placeholder={t('detail.placeholder.addBrand')}
      readOnly={!canEdit}
      display={(_v, label) => label ?? value ?? '-'}
    />
  );
}

// ----------------------------------------------------------------------------
// HeroThumbnail — 96×96 click-to-upload page hero
// ----------------------------------------------------------------------------

function HeroThumbnail({
  productDevId,
  heroUrl,
  altText,
  canEdit,
  uploadFile,
  onSetHero,
}: {
  productDevId: string;
  heroUrl: string | null;
  altText: string;
  canEdit: boolean;
  uploadFile: ReturnType<typeof useProductDevMutations>['uploadFile'];
  /** Pins the uploaded file as the record's hero (hero_file_id). */
  onSetHero: (fileId: string) => Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [heroUrl]);

  const handlePick = async (raw: File) => {
    const file = await prepareUpload(raw, t);
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: t('detail.toast.fileTooLarge.title', { size: formatBytes(file.size) }),
        description: t('detail.toast.fileTooLarge.desc', { max: formatBytes(MAX_UPLOAD_BYTES) }),
        variant: 'destructive',
      });
      return;
    }
    try {
      const { body, type } = await fileToBase64(file);
      const uploaded = await uploadFile.mutateAsync({
        productDevId,
        docType: 'sample_photo',
        fileName: file.name,
        contentType: type,
        body,
      });
      // Pin as hero so variant/colourway photos can't displace it later.
      // Own try: the upload DID succeed here — a pin failure must not
      // report "upload failed" (the photo landed as a fallback hero).
      const fileId = (uploaded as { id?: string } | null | undefined)?.id;
      if (fileId) {
        try {
          await onSetHero(fileId);
        } catch {
          toast({
            title: t('detail.toast.heroPinFailed.title'),
            description: t('detail.toast.heroPinFailed.desc'),
            variant: 'destructive',
          });
        }
      }
    } catch (err) {
      toast({
        title: t('detail.toast.photoUploadFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handlePick(file);
          if (e.target) e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => canEdit && !uploadFile.isPending && inputRef.current?.click()}
        disabled={!canEdit || uploadFile.isPending}
        className={cn(
          'group/hero w-24 h-24 rounded-lg border border-border bg-muted overflow-hidden shrink-0 flex items-center justify-center relative',
          canEdit && 'cursor-pointer hover:border-threadheads-purple/40 transition-colors',
          !canEdit && 'cursor-default',
        )}
        aria-label={heroUrl ? t('detail.aria.replaceHeroPhoto') : t('detail.aria.uploadHeroPhoto')}
      >
        {heroUrl && !imgError ? (
          <img
            src={heroUrl}
            alt={altText}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground/60">
            <ImageIcon className="h-6 w-6" />
            {canEdit && <span className="text-[10px]">{t('detail.hero.addPhoto')}</span>}
          </div>
        )}
        {canEdit && heroUrl && (
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity',
              uploadFile.isPending
                ? 'opacity-100'
                : 'opacity-0 group-hover/hero:opacity-100',
            )}
          >
            {uploadFile.isPending ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : (
              <Upload className="h-4 w-4 text-white" />
            )}
          </span>
        )}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// VariantPhotoCell — 40×40 product photo thumb; falls back to Pantone swatch
// ----------------------------------------------------------------------------

function VariantPhotoCell({
  variantId,
  photoUrl,
  pantoneSwatch,
  pantoneCode,
  approved,
  canEdit,
  uploading,
  onUpload,
}: {
  variantId: string;
  photoUrl: string | null;
  pantoneSwatch: string | null;
  pantoneCode: string | null;
  /** Signed off — renders the emerald check badge on the swatch corner. */
  approved: boolean;
  canEdit: boolean;
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  const { t } = useTranslation('productDev');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [photoUrl]);

  const handleClick = () => {
    if (!canEdit || uploading) return;
    inputRef.current?.click();
  };

  return (
    <div className="relative shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          if (e.target) e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={!canEdit || uploading}
        className={cn(
          'group/photo relative h-10 w-10 rounded-md border border-border overflow-hidden',
          canEdit && 'cursor-pointer hover:border-threadheads-purple/40 transition-colors',
          !canEdit && 'cursor-default',
        )}
        aria-label={photoUrl ? t('detail.aria.replaceVariantPhoto') : t('detail.aria.uploadVariantPhoto')}
        data-variant-id={variantId}
      >
        {photoUrl && !imgError ? (
          <img
            src={photoUrl}
            alt={pantoneCode ?? t('detail.variants.altFallback')}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className="h-full w-full"
            style={{ backgroundColor: pantoneSwatch ?? '#e5e7eb' }}
          />
        )}
        {canEdit && (
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity',
              uploading ? 'opacity-100' : 'opacity-0 group-hover/photo:opacity-100',
            )}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 text-white animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5 text-white" />
            )}
          </span>
        )}
      </button>
      {/* Sign-off mark — OUTSIDE the overflow-hidden button so the ring isn't
          clipped (DESIGN.md §10). Same emerald as the Approved pill. */}
      {approved && (
        <span
          className="absolute -right-1 -top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-background"
          title={t('variantStatus.approved')}
        >
          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} aria-hidden="true" />
          <span className="sr-only">{t('detail.variants.signedOff')}</span>
        </span>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// SizeEditor — tokenised sizes control for a colourway row.
//
// Display: individual size chips (XS S M L …). Edit: a compact text input that
// accepts comma/space-separated tokens AND range shorthand ("XS-3XL"), expanded
// client-side on commit (mirrors the backend ladder), plus a row of quick-add
// toggle chips for the standard ladder so common cases are clicks, not typing.
// Commits through the parent's setVariants persist (which re-heals server-side).
// ----------------------------------------------------------------------------

function SizeEditor({
  sizes,
  canEdit,
  onSave,
}: {
  sizes: string[];
  canEdit: boolean;
  onSave: (next: string[]) => void | Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const enter = () => {
    if (!canEdit || saving) return;
    setDraft(sizes.join(' '));
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const cancel = () => {
    setEditing(false);
    setDraft(sizes.join(' '));
  };

  const commit = async () => {
    // Guard the Enter→disable→blur double-fire: Enter sets saving=true, which
    // disables the focused input, which fires blur → a second commit(). The
    // payload is idempotent but the second round-trip is pure waste.
    if (saving) return;
    const next = expandSizeInput(draft);
    // No net change → just close, no round-trip.
    if (next.length === sizes.length && next.every((s, i) => s === sizes[i])) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // Parent's persist() surfaces the toast; stay in edit mode to retry.
    } finally {
      setSaving(false);
    }
  };

  // Toggle a ladder size in the draft WITHOUT losing input focus (the chip
  // prevents the input's blur-commit via mousedown-prevent).
  const toggle = (size: string) => {
    setDraft(toggleSizeToken(draft, size));
    inputRef.current?.focus();
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={enter}
        disabled={!canEdit}
        className={cn(
          'group flex flex-wrap items-center gap-1 rounded px-1.5 py-1 -mx-1.5 text-left transition-colors',
          canEdit ? 'hover:bg-muted/60 cursor-text' : 'cursor-default',
        )}
        aria-label={canEdit ? t('detail.aria.editSizes') : undefined}
      >
        {sizes.length > 0 ? (
          sizes.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
            >
              {s}
            </span>
          ))
        ) : (
          <span className="text-xs text-muted-foreground italic">
            {t('detail.variants.addSizes')}
          </span>
        )}
        {canEdit && (
          <Pencil
            className="h-3 w-3 ml-0.5 shrink-0 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-hidden
          />
        )}
      </button>
    );
  }

  return (
    <div className="-mx-1.5 space-y-1.5">
      <div className="relative">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          disabled={saving}
          placeholder={t('detail.placeholder.sizes')}
          className="h-7 text-xs px-1.5"
        />
        {saving && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>
      <SizeLadderChips draft={draft} onToggle={toggle} disabled={saving} />
      <p className="text-[10px] text-muted-foreground">{t('detail.variants.sizesHint')}</p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// SizeLadderChips — quick-toggle chip row for the standard ladder, shared by
// SizeEditor (row edit) and the add-colourway form.
// ----------------------------------------------------------------------------

function SizeLadderChips({
  draft,
  onToggle,
  disabled,
}: {
  draft: string;
  onToggle: (size: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('productDev');
  const draftSet = new Set(expandSizeInput(draft));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {SIZE_LADDER.map((s) => {
        const active = draftSet.has(s);
        return (
          <button
            key={s}
            type="button"
            // Keep input focus so a sibling input's blur-commit doesn't fire
            // mid-toggle.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onToggle(s)}
            disabled={disabled}
            aria-pressed={active}
            aria-label={t('detail.aria.toggleSize', { size: s })}
            className={cn(
              'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums transition-colors motion-reduce:transition-none',
              active
                ? 'bg-threadheads-purple/10 text-threadheads-purple ring-1 ring-threadheads-purple/30'
                : 'bg-muted text-muted-foreground hover:bg-muted/70',
            )}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// PantoneCell — inline pantone editor for a colourway row. Display mirrors
// InlineEdit's affordance; edit mode is the PantoneInput picker.
// ----------------------------------------------------------------------------

function PantoneCell({
  value,
  canEdit,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  onSave: (next: string | null) => void | Promise<void>;
}) {
  const { t } = useTranslation('productDev');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const enter = () => {
    if (!canEdit || saving) return;
    setDraft(value ?? '');
    setEditing(true);
  };

  // `override` lets a suggestion pick commit its canonical code immediately,
  // without waiting for the async draft state to settle.
  const commit = async (override?: string) => {
    if (saving) return;
    const next = (override ?? draft).trim() || null;
    if (next === (value ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // Parent's persist() surfaces the toast; stay in edit mode to retry.
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const swatch = pantoneToHex(value);
    return (
      <button
        type="button"
        onClick={enter}
        disabled={!canEdit}
        className={cn(
          'group/pantone flex items-center gap-1.5 rounded px-1.5 py-0.5 -mx-1.5 text-left transition-colors',
          canEdit ? 'hover:bg-muted/60 cursor-text' : 'cursor-default',
        )}
        aria-label={canEdit ? t('detail.aria.editPantone') : undefined}
      >
        {swatch && (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm border border-black/10"
            style={{ backgroundColor: swatch }}
            aria-hidden="true"
          />
        )}
        {value ? (
          <span className="text-[10px] text-muted-foreground font-mono">{value}</span>
        ) : (
          <span className="text-[10px] text-muted-foreground italic">
            {t('detail.placeholder.pantone')}
          </span>
        )}
        {canEdit && (
          <Pencil
            className="h-3 w-3 shrink-0 text-muted-foreground/60 opacity-0 group-hover/pantone:opacity-100 transition-opacity"
            aria-hidden
          />
        )}
      </button>
    );
  }

  return (
    <div className="relative -mx-1.5">
      <PantoneInput
        value={draft}
        onChange={setDraft}
        onPick={(entry) => void commit(entry.code)}
        onCommit={() => void commit()}
        onCancel={() => setEditing(false)}
        placeholder={t('detail.placeholder.pantoneExample')}
        autoFocus
        disabled={saving}
      />
      {saving && (
        <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// VariantsCard — per-row inline editing; always-on "+ Colourway"
// ----------------------------------------------------------------------------

function VariantsCard({
  productDevId,
  canEdit,
  variantsQuery,
  setVariantsSerialized,
  setVariantsQueueDepth,
  getCachedVariants,
  optimisticVariantStatus,
  uploadVariantPhoto,
}: {
  productDevId: string;
  canEdit: boolean;
  variantsQuery: ReturnType<typeof useProductDevVariants>;
  setVariantsSerialized: ReturnType<typeof useProductDevMutations>['setVariantsSerialized'];
  setVariantsQueueDepth: ReturnType<typeof useProductDevMutations>['setVariantsQueueDepth'];
  getCachedVariants: ReturnType<typeof useProductDevMutations>['getCachedVariants'];
  optimisticVariantStatus: ReturnType<typeof useProductDevMutations>['optimisticVariantStatus'];
  uploadVariantPhoto: ReturnType<typeof useProductDevMutations>['uploadVariantPhoto'];
}) {
  const { t } = useTranslation('productDev');
  const rows = useMemo(() => variantsQuery.data ?? [], [variantsQuery.data]);

  type VariantRow = {
    colour: string;
    sizes: string[];
    pantone_code: string | null;
    photo_file_id: string | null;
    swatch_file_id: string | null;
    // Buy-plan quantities must ride along on EVERY whole-array resend, else an
    // unrelated edit (colour rename, pantone, photo) would drop the plan: the
    // backend keys preservation by colour, so a rename can't self-heal.
    size_quantities: Record<string, number>;
    // Per-colourway approval — same clobber-risk as size_quantities; carried on
    // every resend so an unrelated edit never resets it.
    status: string;
  };

  type VariantPatch = Partial<{
    colour: string;
    sizes: string[];
    pantone_code: string | null;
    photo_file_id: string | null;
    status: string;
  }>;

  // Persist by enqueuing a BUILDER into the shared queue (setVariantsSerialized):
  // the builder isn't invoked until this save's turn actually comes up, so it
  // reads `getCachedVariants` — the freshest committed rows, including a
  // buy-plan quantity save that landed while this edit was queued — instead of
  // a snapshot taken when the edit was made. Targets rows by `id` (stable
  // across saves; the server upserts by colour and preserves the row id), not
  // array position, since the fresh array's order/length can differ from what
  // was on screen when the edit was made (a concurrent add/remove shifts it).
  const persist = async (
    patch: { id: string; patch: VariantPatch } | null,
    extra: {
      add?: { colour: string; sizes: string[]; pantone_code: string | null };
    } = {},
    removeId?: string,
  ) => {
    try {
      await setVariantsSerialized(productDevId, () => {
        const currentRows = getCachedVariants(productDevId) ?? rows;
        const next = currentRows
          .map((v): VariantRow | null => {
            if (v.id === removeId) return null;
            const base: VariantRow = {
              colour: v.colour,
              sizes: v.sizes,
              pantone_code: v.pantone_code,
              photo_file_id: v.photo_file_id,
              swatch_file_id: v.swatch_file_id,
              size_quantities: v.size_quantities,
              status: v.status,
            };
            if (patch && patch.id === v.id) {
              return { ...base, ...patch.patch };
            }
            return base;
          })
          .filter((v): v is VariantRow => v != null);

        if (extra.add) {
          next.push({
            ...extra.add,
            photo_file_id: null,
            swatch_file_id: null,
            size_quantities: {},
            status: 'pending',
          });
        }

        return next.map((v, i) => ({
          colour: v.colour,
          sizes: v.sizes,
          sort_order: i,
          pantone_code: v.pantone_code,
          photo_file_id: v.photo_file_id,
          swatch_file_id: v.swatch_file_id,
          size_quantities: v.size_quantities,
          status: v.status,
        }));
      });
    } catch (err) {
      toast({
        title: t('detail.toast.variantSaveFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handlePhotoUpload = async (variantId: string, raw: File) => {
    const file = await prepareUpload(raw, t);
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: t('detail.toast.fileTooLarge.title', { size: formatBytes(file.size) }),
        description: t('detail.toast.fileTooLarge.desc', { max: formatBytes(MAX_UPLOAD_BYTES) }),
        variant: 'destructive',
      });
      return;
    }
    try {
      const { body, type } = await fileToBase64(file);
      await uploadVariantPhoto.mutateAsync({
        variantId,
        fileName: file.name,
        contentType: type,
        body,
      });
    } catch (err) {
      toast({
        title: t('detail.toast.photoUploadFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const [addingOpen, setAddingOpen] = useState(false);
  const [newColour, setNewColour] = useState('');
  const [newSizes, setNewSizes] = useState(DEFAULT_NEW_COLOURWAY_SIZES);
  const [newPantone, setNewPantone] = useState('');

  const resetAddForm = () => {
    setAddingOpen(false);
    setNewColour('');
    setNewSizes(DEFAULT_NEW_COLOURWAY_SIZES);
    setNewPantone('');
  };

  const handleAdd = async () => {
    const colour = newColour.trim();
    if (!colour) return;
    await persist(null, {
      add: {
        colour,
        // Expand ranges / aliases client-side so a new colourway typed as
        // "XS-3XL" lands as individual ladder sizes immediately (server
        // re-heals too).
        sizes: expandSizeInput(newSizes),
        pantone_code: newPantone.trim() || null,
      },
    });
    resetAddForm();
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{t('detail.section.variants')}</CardTitle>
          {rows.length > 0 && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {t('detail.variants.approvedRollup', {
                approved: rows.filter((r) => r.status === 'approved').length,
                total: rows.length,
              })}
            </span>
          )}
        </div>
        {canEdit && !addingOpen && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setAddingOpen(true)}
            className="h-7 px-2 text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('detail.action.colourway')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {variantsQuery.isLoading ? (
          <CardRowsSkeleton rows={2} />
        ) : variantsQuery.error ? (
          <p className="text-sm text-destructive">{variantsQuery.error.message}</p>
        ) : rows.length === 0 && !addingOpen ? (
          <p className="text-xs text-muted-foreground">{t('detail.variants.empty')}</p>
        ) : (
          rows.map((v) => {
            const swatch = pantoneToHex(v.pantone_code);
            return (
              <div key={v.id} className="group flex items-start gap-3 py-2 border-b last:border-b-0">
                <VariantPhotoCell
                  variantId={v.id}
                  photoUrl={v.photo_url}
                  pantoneSwatch={swatch}
                  pantoneCode={v.pantone_code}
                  approved={v.status === 'approved'}
                  canEdit={canEdit}
                  uploading={uploadVariantPhoto.isPending}
                  onUpload={(file) => void handlePhotoUpload(v.id, file)}
                />
                <div className="flex-1 min-w-0 space-y-0.5">
                  <InlineEdit
                    value={v.colour}
                    onSave={(next) =>
                      persist({ id: v.id, patch: { colour: next ?? v.colour } })
                    }
                    placeholder={t('detail.placeholder.colour')}
                    readOnly={!canEdit}
                    displayClassName="text-sm font-medium"
                  />
                  <SizeEditor
                    sizes={v.sizes}
                    canEdit={canEdit}
                    onSave={(next) =>
                      persist({ id: v.id, patch: { sizes: next } })
                    }
                  />
                  <PantoneCell
                    value={v.pantone_code}
                    canEdit={canEdit}
                    onSave={(next) => persist({ id: v.id, patch: { pantone_code: next } })}
                  />
                </div>
                <VariantStatusPill
                  status={v.status}
                  canEdit={canEdit}
                  onCycle={(next) => {
                    // Optimistic cache stamp BEFORE the save: BuyPlanCard
                    // rebuilds its payload from this cache, so a qty-cell blur
                    // inside the save's in-flight window must see the fresh
                    // status, not resend the stale one (review finding).
                    optimisticVariantStatus(productDevId, v.colour, next);
                    void persist({ id: v.id, patch: { status: next } });
                  }}
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => void persist(null, {}, v.id)}
                    className="text-muted-foreground hover:text-destructive p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={t('detail.aria.removeVariant')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}

        {addingOpen && (
          <div className="space-y-2 pt-2 border-t mt-2">
            <Input
              placeholder={t('detail.placeholder.colour')}
              value={newColour}
              onChange={(e) => setNewColour(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
            <div className="space-y-1.5">
              <Input
                placeholder={t('detail.placeholder.sizes')}
                value={newSizes}
                onChange={(e) => setNewSizes(e.target.value)}
                className="h-8 text-sm"
              />
              {/* Pre-filled with the standard XS–3XL run — tap chips to trim
                  or extend instead of typing the ladder out. */}
              <SizeLadderChips
                draft={newSizes}
                onToggle={(size) => setNewSizes(toggleSizeToken(newSizes, size))}
              />
            </div>
            <PantoneInput
              value={newPantone}
              onChange={setNewPantone}
              placeholder={t('detail.placeholder.pantoneExample')}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={resetAddForm}
                className="h-7 px-2 text-xs"
              >
                {t('detail.action.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleAdd()}
                disabled={!newColour.trim() || setVariantsQueueDepth > 0}
                className="h-7 px-2 text-xs"
              >
                {setVariantsQueueDepth > 0 ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" /> {t('detail.action.add')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// AttributesCard — per-kind inline edit; auto-save on blur
// ----------------------------------------------------------------------------

function AttributesCard({
  productDevId,
  canEdit,
  attributesQuery,
  setAttributes,
}: {
  productDevId: string;
  canEdit: boolean;
  attributesQuery: ReturnType<typeof useProductDevAttributes>;
  setAttributes: ReturnType<typeof useProductDevMutations>['setAttributes'];
}) {
  const { t } = useTranslation('productDev');
  const byKind = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const row of attributesQuery.data ?? []) {
      (map[row.kind] ??= []).push(row.value);
    }
    return map;
  }, [attributesQuery.data]);

  const persistKind = async (kind: AttributeKind, raw: string | null) => {
    const values = (raw ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await setAttributes.mutateAsync({ productDevId, kind, values });
    } catch (err) {
      toast({
        title: t('detail.toast.attributeSaveFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('detail.section.attributes')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {attributesQuery.isLoading ? (
          <CardRowsSkeleton rows={2} />
        ) : attributesQuery.error ? (
          <p className="text-sm text-destructive">{attributesQuery.error.message}</p>
        ) : (
        DISPLAY_ATTRIBUTE_KINDS.map((kind) => {
          const current = byKind[kind] ?? [];
          return (
            <div key={kind} className="space-y-1">
              <span className="text-xs text-muted-foreground font-medium">
                {t(`attributeKind.${kind}`)}
              </span>
              {current.length > 0 ? (
                <InlineEdit
                  value={current.join(', ')}
                  onSave={(v) => persistKind(kind, v)}
                  placeholder={t('detail.placeholder.commaSeparated')}
                  readOnly={!canEdit}
                  display={(v) => (
                    <span className="flex flex-wrap gap-1">
                      {(v ?? '')
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean)
                        .map((t, i) => (
                          <span
                            key={`${kind}-${i}-${t}`}
                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px]"
                          >
                            {t}
                          </span>
                        ))}
                    </span>
                  )}
                />
              ) : (
                <InlineEdit
                  value={null}
                  onSave={(v) => persistKind(kind, v)}
                  placeholder={t('detail.attributes.addPlaceholder', {
                    kind: t(`attributeKind.${kind}`).toLowerCase(),
                  })}
                  readOnly={!canEdit}
                  displayClassName="text-[11px]"
                />
              )}
            </div>
          );
        })
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// FilesCard — categorized file lists (multiple files per category)
//
// Each named category row keeps the quiet click/drop affordance from the
// slot era, but a category now holds ALL of its files (newest-first, with
// version badges) instead of just the latest. Uploads APPEND — picking or
// dropping N files uploads all of them sequentially into that category.
// 'Other' uploads go through the header uploader, which offers an optional
// custom label.
// ----------------------------------------------------------------------------

type ProductDevFileRow = NonNullable<ReturnType<typeof useProductDevFiles>['data']>[number];

function FilesCard({
  productDevId,
  canEdit,
  filesQuery,
  uploadFile,
  deleteFile,
  utils,
}: {
  productDevId: string;
  canEdit: boolean;
  filesQuery: ReturnType<typeof useProductDevFiles>;
  uploadFile: ReturnType<typeof useProductDevMutations>['uploadFile'];
  deleteFile: ReturnType<typeof useProductDevMutations>['deleteFile'];
  utils: ReturnType<typeof trpc.useUtils>;
}) {
  const { t } = useTranslation('productDev');
  // Header uploader panel (doc-type select + optional label for 'other').
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docType, setDocType] = useState<string>('tech_pack');
  const [otherLabel, setOtherLabel] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const panelInputRef = useRef<HTMLInputElement | null>(null);

  // Per-category direct upload (click or drop on a category row).
  const [pendingSlot, setPendingSlot] = useState<string | null>(null);
  const [dragSlot, setDragSlot] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const slotInputRef = useRef<HTMLInputElement | null>(null);

  const filesByDocType = useMemo(() => {
    // The DAO orders (doc_type asc, version desc, uploaded_at desc), so each
    // category's array is already newest-first.
    const map: Record<string, ProductDevFileRow[]> = {};
    for (const f of filesQuery.data ?? []) {
      (map[f.doc_type] ??= []).push(f);
    }
    return map;
  }, [filesQuery.data]);

  const otherFiles = useMemo(
    () =>
      (filesQuery.data ?? []).filter(
        (f) =>
          !NAMED_DOC_SLOTS.includes(f.doc_type) &&
          !NON_DOCUMENT_DOC_TYPES.includes(f.doc_type),
      ),
    [filesQuery.data],
  );

  // The "{filled} of N" counter counts displayed categories with at least one
  // file (sample photos are excluded from the Files card — they live with
  // Samples).
  const filledSlotCount = DISPLAY_DOC_SLOTS.filter(
    (s) => (filesByDocType[s]?.length ?? 0) > 0,
  ).length;

  // Ids whose <img> preview failed to decode → render an extension label tile.
  const [imgErrorIds, setImgErrorIds] = useState<Set<string>>(new Set());
  const markImgError = (id: string) =>
    setImgErrorIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

  /** Sequential append-upload of N files into one category. */
  const uploadMany = async (
    targetDocType: string,
    rawFiles: File[],
    label?: string | null,
  ): Promise<boolean> => {
    // Convert any HEIC/HEIF up front so the size checks below run on the
    // converted bytes (a single seam for pick + drop + panel paths).
    const files = await Promise.all(rawFiles.map((f) => prepareUpload(f, t)));
    const oversize = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
    for (const f of oversize) {
      toast({
        title: t('detail.toast.fileTooLargeNamed.title', {
          name: f.name,
          size: formatBytes(f.size),
        }),
        description: t('detail.toast.fileTooLargeNamed.desc', {
          max: formatBytes(MAX_UPLOAD_BYTES),
        }),
        variant: 'destructive',
      });
    }
    const valid = files.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (valid.length === 0) return false;

    setUploadingSlot(targetDocType);
    let failures = 0;
    try {
      for (const f of valid) {
        try {
          const { body, type } = await fileToBase64(f);
          await uploadFile.mutateAsync({
            productDevId,
            docType: targetDocType,
            fileName: f.name,
            contentType: type,
            body,
            label: label?.trim() ? label.trim() : undefined,
          });
        } catch (err) {
          failures += 1;
          toast({
            title: t('detail.toast.uploadFailed.title', { name: f.name }),
            description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
            variant: 'destructive',
          });
        }
      }
    } finally {
      setUploadingSlot(null);
    }
    return failures === 0;
  };

  const closeUploader = () => {
    setUploadOpen(false);
    setStagedFiles([]);
    setDocType('tech_pack');
    setOtherLabel('');
    setDragOver(false);
  };

  const handlePanelSave = async () => {
    if (stagedFiles.length === 0) {
      toast({ title: t('detail.toast.pickFileFirst.title'), variant: 'destructive' });
      return;
    }
    const ok = await uploadMany(
      docType,
      stagedFiles,
      docType === 'other' ? otherLabel : undefined,
    );
    if (ok) closeUploader();
  };

  const handleDownload = async (fileId: string) => {
    try {
      const result = await utils.client.productDev.getFileDownloadUrl.query({ fileId });
      window.open(result.url, '_blank', 'noopener');
    } catch (err) {
      toast({
        title: t('detail.toast.downloadFailed.title'),
        description: err instanceof Error ? err.message : t('detail.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const startSlotUpload = (slotDocType: string) => {
    setPendingSlot(slotDocType);
    slotInputRef.current?.click();
  };

  // Dropping file(s) directly onto a category row appends them all.
  const handleSlotDrop = (slotDocType: string, e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragSlot(null);
    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length > 0) void uploadMany(slotDocType, dropped);
  };

  const handlePanelDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files ?? []);
    if (dropped.length > 0) setStagedFiles((prev) => [...prev, ...dropped]);
  };

  const slotDragProps = (slot: string) => ({
    onDragOver: (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setDragSlot(slot);
    },
    onDragLeave: () => setDragSlot((s) => (s === slot ? null : s)),
    onDrop: (e: React.DragEvent<HTMLElement>) => handleSlotDrop(slot, e),
  });

  const renderFileRow = (f: ProductDevFileRow, options: { showDocType?: boolean } = {}) => {
    const isImage =
      f.signed_url &&
      f.content_type?.toLowerCase().startsWith('image/') &&
      !imgErrorIds.has(f.id);
    return (
      <div
        key={f.id}
        className="group flex items-center gap-2.5 py-1.5 px-1.5 -mx-1.5 rounded-md hover:bg-muted/40 transition-colors"
      >
        {isImage ? (
          <button
            type="button"
            onClick={() => handleDownload(f.id)}
            className="h-9 w-9 rounded-md border border-border bg-muted overflow-hidden shrink-0 hover:opacity-80 transition-opacity"
            title={t('detail.files.openFullSize')}
          >
            <img
              src={f.signed_url!}
              alt={f.file_name}
              className="h-full w-full object-cover"
              onError={() => markImgError(f.id)}
            />
          </button>
        ) : (
          // Non-image doc, or an image that failed to decode (e.g. HEIC): show
          // the file-type extension as a small label tile, never a broken img.
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
            aria-hidden="true"
          >
            {fileExtLabel(f.file_name)}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-medium truncate">{f.label ?? f.file_name}</span>
            <span className="shrink-0 font-mono text-[10px] text-muted-foreground bg-muted px-1 py-px rounded">
              v{f.version}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground truncate">
            {options.showDocType && `${t(`docType.${f.doc_type}`)} · `}
            {f.label != null && `${f.file_name} · `}
            {f.file_size != null && `${formatBytes(f.file_size)} · `}
            {new Date(f.uploaded_at).toLocaleDateString()}
            {f.uploaded_by_name && ` · ${f.uploaded_by_name}`}
          </div>
        </div>
        {/* Actions reveal on hover / keyboard focus (desktop admin surface). */}
        <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => handleDownload(f.id)}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label={t('detail.aria.download')}
            title={t('detail.aria.download')}
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => deleteFile.mutate({ fileId: f.id })}
              className="text-muted-foreground hover:text-destructive p-1"
              aria-label={t('detail.aria.delete')}
              title={t('detail.aria.delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('detail.section.files')}</CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground tabular-nums">
            {t('detail.files.counter', {
              filled: filledSlotCount,
              total: DISPLAY_DOC_SLOTS.length,
            })}
          </span>
          {canEdit && !uploadOpen && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setUploadOpen(true)}
              className="h-7 px-2 text-xs"
            >
              <Upload className="h-3 w-3 mr-1" /> {t('detail.action.upload')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Hidden multi-file input shared by the category rows. */}
        <input
          ref={slotInputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length > 0 && pendingSlot) void uploadMany(pendingSlot, picked);
            setPendingSlot(null);
            if (e.target) e.target.value = '';
          }}
        />

        {uploadOpen && (
          <div className="space-y-3 rounded-md border border-threadheads-purple/20 bg-threadheads-purple/[0.03] p-3 mb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">{t('detail.files.docType')}</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPE_OPTIONS.map((dt) => (
                      <SelectItem key={dt} value={dt}>
                        {t(`docType.${dt}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={closeUploader}
                className="text-muted-foreground hover:text-foreground p-1 mt-0.5"
                aria-label={t('detail.aria.cancel')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {docType === 'other' && (
              <div className="space-y-1">
                <Label className="text-xs">
                  {t('detail.files.label')}{' '}
                  <span className="text-muted-foreground font-normal">{t('detail.files.optional')}</span>
                </Label>
                <Input
                  value={otherLabel}
                  onChange={(e) => setOtherLabel(e.target.value)}
                  placeholder={t('detail.placeholder.labelExample')}
                  className="h-8 text-xs"
                  maxLength={120}
                />
              </div>
            )}

            <input
              ref={panelInputRef}
              type="file"
              multiple
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length > 0) setStagedFiles((prev) => [...prev, ...picked]);
                if (e.target) e.target.value = '';
              }}
              className="sr-only"
            />
            {stagedFiles.length > 0 && (
              <div className="space-y-1.5">
                {stagedFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2 rounded-md border bg-background px-3 py-2"
                  >
                    <FileText className="h-4 w-4 text-threadheads-purple shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.name}</p>
                      <p className="text-[10px] text-muted-foreground">{formatBytes(f.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      className="text-muted-foreground hover:text-destructive p-1"
                      aria-label={t('detail.aria.removeFile', { name: f.name })}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => panelInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handlePanelDrop}
              className={cn(
                'w-full flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed py-4 px-3 transition-colors text-center',
                dragOver
                  ? 'border-threadheads-purple bg-threadheads-purple/5 text-threadheads-purple'
                  : 'border-muted-foreground/20 text-muted-foreground hover:border-threadheads-purple/40 hover:text-foreground',
              )}
            >
              <Upload className="h-4 w-4" />
              <span className="text-xs font-medium">
                {stagedFiles.length > 0
                  ? t('detail.files.addMore')
                  : t('detail.files.dropzone')}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {t('detail.files.sizeHint', { max: formatBytes(MAX_UPLOAD_BYTES) })}
              </span>
            </button>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={closeUploader}
                className="h-7 px-2 text-xs"
              >
                {t('detail.action.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handlePanelSave()}
                disabled={stagedFiles.length === 0 || uploadFile.isPending}
                className="h-7 px-3 text-xs"
              >
                {uploadFile.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-3 w-3 mr-1" />
                    {stagedFiles.length > 1
                      ? t('detail.files.saveN', { count: stagedFiles.length })
                      : t('detail.action.save')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {filesQuery.isLoading ? (
          <CardRowsSkeleton rows={3} />
        ) : (
          <>
            {DISPLAY_DOC_SLOTS.map((slot) => {
              const slotFiles = filesByDocType[slot] ?? [];
              const isUploadingHere = uploadingSlot === slot;

              if (slotFiles.length === 0 && !isUploadingHere) {
                if (!canEdit) return null;
                // Empty category: the whole row is a quiet click/drop target.
                return (
                  <div
                    key={slot}
                    role="button"
                    tabIndex={0}
                    onClick={() => startSlotUpload(slot)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startSlotUpload(slot);
                      }
                    }}
                    {...slotDragProps(slot)}
                    className={cn(
                      'group flex items-center gap-2.5 py-2 px-1.5 -mx-1.5 rounded-md cursor-pointer text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      dragSlot === slot
                        ? 'bg-threadheads-purple/5 ring-1 ring-threadheads-purple/30'
                        : 'hover:bg-muted/40',
                    )}
                  >
                    <Circle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                    <span className="text-sm text-muted-foreground flex-1 group-hover:text-foreground transition-colors">
                      {t(`docType.${slot}`)}
                    </span>
                    <span
                      className={cn(
                        'text-[11px] transition-colors',
                        dragSlot === slot
                          ? 'text-threadheads-purple'
                          : 'text-muted-foreground/50 group-hover:text-threadheads-purple',
                      )}
                    >
                      {dragSlot === slot ? t('detail.files.dropToUpload') : t('detail.files.dropOrClick')}
                    </span>
                  </div>
                );
              }

              // Filled category: header row stays a click/drop target for
              // appending; ALL files render newest-first beneath it.
              return (
                <div key={slot} className="pb-1">
                  <div
                    role={canEdit ? 'button' : undefined}
                    tabIndex={canEdit ? 0 : undefined}
                    onClick={() => canEdit && startSlotUpload(slot)}
                    onKeyDown={(e) => {
                      if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        startSlotUpload(slot);
                      }
                    }}
                    {...(canEdit ? slotDragProps(slot) : {})}
                    className={cn(
                      'group flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded-md transition-colors',
                      canEdit &&
                        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      dragSlot === slot
                        ? 'bg-threadheads-purple/5 ring-1 ring-threadheads-purple/30'
                        : canEdit && 'hover:bg-muted/40',
                    )}
                  >
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex-1">
                      {t(`docType.${slot}`)}
                      {slotFiles.length > 1 && (
                        <span className="ml-1.5 normal-case tracking-normal text-muted-foreground/60 tabular-nums">
                          ×{slotFiles.length}
                        </span>
                      )}
                    </span>
                    {isUploadingHere ? (
                      <Loader2 className="h-3 w-3 animate-spin text-threadheads-purple" />
                    ) : (
                      canEdit && (
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 text-[11px] transition-all',
                            dragSlot === slot
                              ? 'text-threadheads-purple opacity-100'
                              : 'text-muted-foreground/50 opacity-0 group-hover:opacity-100 group-hover:text-threadheads-purple',
                          )}
                        >
                          <Plus className="h-3 w-3" />
                          {dragSlot === slot ? t('detail.files.dropToAdd') : t('detail.files.dropOrClick')}
                        </span>
                      )
                    )}
                  </div>
                  {slotFiles.map((f) => renderFileRow(f))}
                </div>
              );
            })}

            {(otherFiles.length > 0 || uploadingSlot === 'other') && (
              <div className="border-t border-muted-foreground/10 mt-2 pt-2 space-y-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium flex-1">
                    {t('detail.files.other')}
                    {otherFiles.length > 1 && (
                      <span className="ml-1.5 normal-case tracking-normal text-muted-foreground/60 tabular-nums">
                        ×{otherFiles.length}
                      </span>
                    )}
                  </p>
                  {uploadingSlot === 'other' && (
                    <Loader2 className="h-3 w-3 animate-spin text-threadheads-purple" />
                  )}
                </div>
                {otherFiles.map((f) => renderFileRow(f, { showDocType: true }))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
